import { useState } from 'react';
import { Wand2 } from 'lucide-react';
import { RegisterScreen } from '@/components/RegisterScreen';
import type { FieldSpec, FormValues } from '@/components/ChildRecords';
import { StatusPill, formatDate, todayIso } from '@/components/live';
import { callRpc } from '@/api/resources';
import { usePickers } from '@/hooks/usePickers';
import { useRows } from '@/hooks/useResource';
import { toast } from '@/lib/toast';
import { MONITORING_INDICATORS, MONITORING_STATUS, SAMPLES, financialYear, optionLabel } from '@/lib/practiceLists';

interface MonitoringRow {
  id: string; monitoring_code: string | null; plot_code: string | null; farmer_name: string | null; village_name: string | null;
  period_label: string; field_visit_date: string | null; harvest_in_period: string; adverse_findings: number; verified_by_name: string;
  record_status: string; landscape_screening_result: string;
}

const ADVERSE = MONITORING_INDICATORS.filter((indicator) => indicator.adverse).map((indicator) => indicator.key);

/**
 * Annual monitoring record, one per plot and period (owner, 2026-09-21): the indicator set in the
 * owner's order, each against its FSC criteria, verified by the area in-charge, with comments.
 * "Prefill from records" reads what the registers already say for the plot in the period
 * (accidents, complaints, inputs, growth checks, harvests, consultations, the plot's HCV and
 * customary-rights entries); the person verifying confirms every answer.
 */
export default function AnnualMonitoringScreen() {
  const pick = usePickers(['plots', 'users']);
  const consultationRows = useRows<{ id: string; consultation_code: string | null; reference_no: string | null; held_on: string; village_name: string | null }>(
    'stakeholder_consultations', { orderBy: { column: 'held_on', ascending: false } });
  const consultations = (consultationRows.data ?? []).map((row) => ({
    id: row.id, label: `${row.reference_no ?? row.consultation_code ?? ''} - ${row.held_on}${row.village_name ? ` - ${row.village_name}` : ''}`,
  }));
  const fy = financialYear();
  const [busy, setBusy] = useState(false);

  async function prefill(form: FormValues, setForm: (update: (now: FormValues) => FormValues) => void) {
    if (!form.plot_id || !form.period_from || !form.period_to) {
      toast.error('Choose the plot and the period first.');
      return;
    }
    setBusy(true);
    try {
      // The registers' answers, and the planting stock off the sapling register (10.4.1: whether
      // the supplying nursery's non-GMO declaration is held - owner, 2026-09-27).
      const args = { p_plot_id: form.plot_id, p_from: form.period_from, p_to: form.period_to };
      const [registers, stock] = await Promise.all([
        callRpc<Record<string, string | null>>('fn_annual_monitoring_prefill', args),
        callRpc<Record<string, string | null>>('fn_annual_monitoring_stock_prefill', args),
      ]);
      const facts = { ...(registers ?? {}), ...(stock ?? {}) };
      setForm((now) => {
        const next: FormValues = { ...now };
        for (const [key, value] of Object.entries(facts ?? {})) {
          if (value != null) next[key] = String(value);
        }
        if (next.harvest_in_period === 'no') {
          next.ppe_verified_at_harvest = 'not_harvested';
          next.worker_age_id_verified = 'not_harvested';
        }
        if (next.biopesticide_applied !== 'yes') next.biopesticide_screened = 'not_applicable';
        const picked = (consultationRows.data ?? []).find((row) => row.id === next.consultation_id);
        if (picked?.reference_no && !next.village_consultation_ref) next.village_consultation_ref = picked.reference_no;
        return next;
      });
      toast.success('Filled from the records on file. Check each answer before saving.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'The records could not be read.');
    } finally {
      setBusy(false);
    }
  }

  const indicatorFields: FieldSpec[] = MONITORING_INDICATORS.map((indicator, index) => ({
    key: indicator.key, label: `${index + 1}. ${indicator.label} [${indicator.refs}]`, type: 'select', options: indicator.options, required: true,
    section: index === 0 ? 'Indicators' : undefined,
  }));
  // Details that go with some answers, placed right after them.
  const withDetails: FieldSpec[] = [];
  for (const field of indicatorFields) {
    withDetails.push(field);
    if (field.key === 'landscape_screening_result') {
      withDetails.push({ key: 'screening_source_set', label: 'Screening source set applied [9.4.2]', type: 'text', suggestions: SAMPLES.screeningSource });
    }
    if (field.key === 'rte_screening_checklist') {
      withDetails.push({ key: 'rte_screening_ref', label: 'RTE checklist reference', type: 'text', suggestions: SAMPLES.documentRef, showIf: (form) => form.rte_screening_checklist === 'yes' });
      withDetails.push({ key: 'consultation_id', label: 'Village-level consultation [4.4.1 / 9.4.2]', type: 'select', options: consultations,
        placeholder: consultations.length ? 'Pick from the consultation register' : 'None in the register yet' });
      withDetails.push({ key: 'village_consultation_ref', label: 'Consultation reference no.', type: 'text', suggestions: SAMPLES.referenceNo,
        hint: 'Filled from the consultation picked; type it if the meeting is not in the register.' });
    }
    if (field.key === 'nursery_non_gmo_declaration') {
      withDetails.push({ key: 'nursery_declaration_ref', label: 'Declaration reference', type: 'text', suggestions: SAMPLES.documentRef, showIf: (form) => form.nursery_non_gmo_declaration === 'yes' });
    }
    if (field.key === 'growth_survival_monitored') {
      withDetails.push({ key: 'field_visit_date', label: 'Field visit date in the period [1.4.2 / 9.4.3]', type: 'date', max: todayIso() });
    }
  }

  return (
    <RegisterScreen<MonitoringRow>
      secondary={{ sum: (row) => row.adverse_findings, unit: 'adverse', digits: 0 }}
      addLabel="Add annual monitoring" exportName="annual-monitoring" recordLabel={(row) => `monitoring ${row.monitoring_code ?? ''} - ${row.plot_code ?? ''} ${row.period_label}`}
      screenKey="plot.register" searchColumns={['monitoring_code', 'plot_code', 'farmer_name', 'period_label', 'verified_by_name']}
      subtitle="One record per plot and year: the FSC indicator set, verified by the area in-charge."
      table="ops.plot_annual_monitoring" title="Annual monitoring" view="plot_annual_monitoring"
      columns={[
        { key: 'monitoring_code', label: 'Ref', render: (row) => <strong>{row.monitoring_code ?? '--'}</strong> },
        { key: 'plot_code', label: 'Plot', render: (row) => <span className="cell-stack"><strong>{row.plot_code ?? '--'}</strong><small className="muted">{row.farmer_name ?? ''}</small></span> },
        { key: 'period_label', label: 'Period', render: (row) => row.period_label },
        { key: 'field_visit_date', label: 'Field visit', render: (row) => formatDate(row.field_visit_date) },
        { key: 'harvest_in_period', label: 'Harvest', render: (row) => (row.harvest_in_period === 'yes' ? 'Yes' : 'No') },
        { key: 'adverse_findings', label: 'Adverse findings', align: 'right', render: (row) => (row.adverse_findings ? <span className="field-error">{row.adverse_findings}</span> : '0') },
        { key: 'verified_by_name', label: 'Verified by', render: (row) => row.verified_by_name },
        { key: 'record_status', label: 'Status', render: (row) => <StatusPill value={row.record_status} /> },
      ]}
      stats={[
        { key: 'draft', label: 'Draft', tone: 'info', match: (row) => row.record_status === 'draft' },
        { key: 'verified', label: 'Verified', tone: 'ok', match: (row) => row.record_status === 'verified' },
        { key: 'adverse', label: 'Adverse findings', tone: 'error', match: (row) => row.adverse_findings > 0 },
        { key: 'this_year', label: fy.label, tone: 'neutral', match: (row) => row.period_label === fy.label },
      ]}
      defaults={{
        period_label: fy.label, period_from: fy.from, period_to: fy.to, record_status: 'draft',
        ...Object.fromEntries(MONITORING_INDICATORS.map((indicator) => [indicator.key, ''])),
      }}
      fields={[
        { key: 'plot_id', label: 'Plot', type: 'select', options: pick.plots, required: true },
        { key: 'period_label', label: 'Period', type: 'text', required: true, suggestions: [fy.label] },
        { key: 'period_from', label: 'From', type: 'date', required: true },
        { key: 'period_to', label: 'To', type: 'date', required: true },
        ...withDetails,
        { key: 'verified_by_id', label: 'Verified by (area in-charge)', type: 'select', options: pick.users, section: 'Verification' },
        { key: 'verified_by_name', label: 'Name as signed', type: 'text', required: true, placeholder: 'e.g. Sanjay Patel' },
        { key: 'verified_on', label: 'Verified on', type: 'date', max: todayIso(), required: (form) => form.record_status === 'verified' },
        { key: 'comments', label: 'Comments', type: 'textarea', required: (form) => ADVERSE.some((key) => form[key] === 'yes'),
          hint: 'Required when any adverse finding is Yes: what was found and what was done.' },
        { key: 'record_status', label: 'Status', type: 'select', options: MONITORING_STATUS, required: true },
      ]}
      toolbar={(form, setForm) => (
        <div className="toolbar-row">
          <button disabled={busy} onClick={() => void prefill(form, setForm)} type="button"><Wand2 size={15} /> {busy ? 'Reading records...' : 'Prefill from records'}</button>
          <span className="muted">Accidents, complaints, inputs, growth checks, harvests, consultations and saplings issued on file for this plot and period.</span>
        </div>
      )}
      validate={(form) => ({
        period_label: /^\d{4}-\d{2}$/.test(String(form.period_label)) ? undefined : 'Write the period as 2026-27.',
        period_to: String(form.period_to) <= String(form.period_from) ? 'The period must end after it starts.' : undefined,
        field_visit_date: form.field_visit_date && (String(form.field_visit_date) < String(form.period_from) || String(form.field_visit_date) > String(form.period_to))
          ? 'The field visit must fall inside the period.' : undefined,
        ppe_verified_at_harvest: form.harvest_in_period === 'no' && form.ppe_verified_at_harvest && form.ppe_verified_at_harvest !== 'not_harvested'
          ? 'No harvest in the period: choose "Not harvested in period".'
          : form.harvest_in_period === 'yes' && form.ppe_verified_at_harvest === 'not_harvested' ? 'There was a harvest: say whether PPE was verified.' : undefined,
        worker_age_id_verified: form.harvest_in_period === 'no' && form.worker_age_id_verified && form.worker_age_id_verified !== 'not_harvested'
          ? 'No harvest in the period: choose "Not harvested in period".'
          : form.harvest_in_period === 'yes' && form.worker_age_id_verified === 'not_harvested' ? 'There was a harvest: say whether ages and IDs were checked.' : undefined,
        biopesticide_screened: form.biopesticide_applied !== 'yes' && form.biopesticide_screened && form.biopesticide_screened !== 'not_applicable'
          ? 'No biopesticide applied: choose Not applicable.' : undefined,
      })}
    />
  );
}

export { optionLabel };
